import { state } from '../map_state/state'
import { Renderer } from '../renderer'
import { callMethodOnKeydown, screenCssToWorld, canvasBackingToCss, worldToScreen, isTyping } from '../helpers'
import { loader } from '../map_state/loader'
import { viewport } from '../map_state/viewport'
import { buildCostMap, traceAStar, removeJaggedness, pathLengthPx } from '../curve_distance_tracer'
import { panTool } from './pan_tool'
import { addSensorTool } from './add_sensor_tool'
import { cropTool } from './crop_tool'
import { pointDistanceTool } from './point_distance_tool'
import { linearFlowTool } from './linear_flow_tool'
import { dragTool } from './drag_tool'
import { edgeDistanceTool } from './edge_distance_tool'
import { pathcalculatorTool } from './path_calculator_tool'
class PathTracerTool {
  constructor () {
    this.curveElement = document.getElementById('mct-curve-distance')
    this.lineElement = document.getElementById('mct-line-distance')
    this.edgeElement = document.getElementById('mct-edge-distance-tracer')
    this.eraserElement = document.getElementById('mct-remove-calculated-path')
    this.dimensionElement = document.getElementById('mct-calculate-distance-container')
    this.bindEvents()
  }

  bindEvents () {
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onClick = this.onClick.bind(this)
    this.curveDistanceTracer = this.curveDistanceTracer.bind(this)
    this.lineDistanceTracer = this.lineDistanceTracer.bind(this)
    this.edgeDistanceTracer = this.edgeDistanceTracer.bind(this)
    this.removePath = this.removePath.bind(this)

    this.curveElement.addEventListener('click', this.curveDistanceTracer)
    this.lineElement.addEventListener('click', this.lineDistanceTracer)
    this.edgeElement.addEventListener('click', this.edgeDistanceTracer)
    this.eraserElement.addEventListener('click', this.removePath)
    document.addEventListener('keydown', this.onKeyDown)
  }

  unBindEvents () {
    this.curveElement.removeEventListener('click', this.curveDistanceTracer)
    this.lineElement.removeEventListener('click', this.lineDistanceTracer)
    this.edgeElement.removeEventListener('click', this.edgeDistanceTracer)
    this.eraserElement.removeEventListener('click', this.removePath)
    document.removeEventListener('keydown', this.onKeyDown)
  }

  onKeyDown (e) {
    if (isTyping(e.target)) return
    if (e.key === '1') callMethodOnKeydown(e.target, this.curveDistanceTracer)
    if (e.key === '2') callMethodOnKeydown(e.target, this.lineDistanceTracer)
    if (e.key === '3') callMethodOnKeydown(e.target, this.edgeDistanceTracer)
    if (e.key === 'v') callMethodOnKeydown(e.target, this.removePath)
  }

  toggle () {
    this.curveElement.classList.toggle('hidden', (state.tracerMode === 'edge' && state.tracerPathWorld.length == 2) || (state.tracerMode !== 'edge' && state.tracerPointsWorld.length == 2))
    this.lineElement.classList.toggle('hidden', (state.tracerMode === 'edge' && state.tracerPathWorld.length == 2) || (state.tracerMode !== 'edge' && state.tracerPointsWorld.length == 2))
    this.edgeElement.classList.toggle('hidden', (state.tracerMode === 'edge' && state.tracerPathWorld.length == 2) || (state.tracerMode !== 'edge' && state.tracerPointsWorld.length == 2))
    this.eraserElement.classList.toggle('hidden', (state.tracerMode !== 'edge' && state.tracerPointsWorld.length != 2) || (state.tracerMode === 'edge' && state.tracerPathWorld.length != 2))
    this.dimensionElement.classList.toggle('hidden', (state.tracerMode !== 'edge' && state.tracerPointsWorld.length != 2) || (state.tracerMode === 'edge' && state.tracerPathWorld.length != 2))
  }

  canTrace () {
    if (!state.mapImage) {
      loader.setStatusMessage('failure', 'Load a map image to use the path tracer tool.')
      return false
    }
    return true
  }

  onClick (event) {
    if (!state.tracerMode || state.tracerPointsWorld.length >= 2) return

    if (state.tracerMode === 'edge') {
      this.detach()
      this.toggle()
    }

    const rect = viewport.canvas.getBoundingClientRect()
    const world = screenCssToWorld({ x: event.clientX - rect.left, y: event.clientY - rect.top })
    if (state.tracerMode !== 'edge') state.tracerPointsWorld = [...state.tracerPointsWorld, world]

    Renderer.render()

    if (state.tracerPointsWorld.length < 2) return

    this.detach()
    this.toggle()

    if (state.tracerMode === 'curve') this.traceAndSetPath()
    if (state.tracerMode === 'line') this.traceLinearPath()
  }

  reset () {
    state.tracerMode = null
    state.tracerPointsWorld = []
    state.tracerPathWorld = []
    this.toggle()
  }

  curveDistanceTracer (enabled = true) {
    this.enableMode('curve', enabled)
  }

  lineDistanceTracer (enabled = true) {
    this.enableMode('line', enabled)
  }

  edgeDistanceTracer (enabled = true) {
    this.enableMode('edge', enabled)
  }

  attach () {
    if (state.tracerMode === 'edge') {
      viewport.canvas.addEventListener('mousemove', this.onMouseMove)
    }
    viewport.canvas.addEventListener('click', this.onClick)
  }

  detach () {
    viewport.canvas.removeEventListener('click', this.onClick)
    viewport.canvas.removeEventListener('mousemove', this.onMouseMove)
  }

  onMouseMove (event) {
    if (state.tracerMode !== 'edge') return

    const rect = viewport.canvas.getBoundingClientRect()
    const mouseWorld = screenCssToWorld({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    })

    const x = state.mapWorld.x
    const y = state.mapWorld.y
    const w = state.mapImage.naturalWidth
    const h = state.mapImage.naturalHeight

    const edges = [
      {
        start: { x, y },
        end: { x: x + w, y }
      },
      {
        start: { x: x + w, y },
        end: { x: x + w, y: y + h }
      },
      {
        start: { x, y: y + h },
        end: { x: x + w, y: y + h }
      },
      {
        start: { x, y },
        end: { x, y: y + h }
      }
    ]

    let nearestEdge = null
    let nearestDistance = Infinity

    edges.forEach(edge => {
      const distance = this.pointToSegmentDistance(
        mouseWorld,
        edge.start,
        edge.end
      )

      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestEdge = edge
      }
    })

    if (!nearestEdge) return

    state.tracerPathWorld = [nearestEdge.start, nearestEdge.end]
    state.lenPath = Math.hypot(
      nearestEdge.end.x - nearestEdge.start.x,
      nearestEdge.end.y - nearestEdge.start.y
    )
    state.lenUnit = 'px'

    Renderer.render()
  }

  pointToSegmentDistance (point, start, end) {
    const dx = end.x - start.x
    const dy = end.y - start.y

    if (dx === 0 && dy === 0) {
      return Math.hypot(point.x - start.x, point.y - start.y)
    }

    const t = (
      ((point.x - start.x) * dx + (point.y - start.y) * dy) /
            (dx * dx + dy * dy)
    )

    const clampedT = Math.max(0, Math.min(1, t))

    const closestX = start.x + clampedT * dx
    const closestY = start.y + clampedT * dy

    return Math.hypot(point.x - closestX, point.y - closestY)
  }

  enableMode (mode, enabled) {
    if (!this.canTrace()) return

    this.curveElement.classList.remove('selected')
    this.lineElement.classList.remove('selected')
    this.edgeElement.classList.remove('selected')
    if (state.tracerMode != mode && enabled) {
      if (state.panMode) panTool.activate(false)
      else if (state.addSensorMode) addSensorTool.activate(false)
      else if (state.cropMode) cropTool.activate(false)
      else if (state.dragMode) dragTool.activate(false)
      else if (state.linearFlowMode) linearFlowTool.activate(false)
      if (state.pointDistanceMode) pointDistanceTool.activate(false)
      if (state.edgeDistanceMode) edgeDistanceTool.activate(false)
      this.reset()
      state.tracerMode = mode
      state.calibration = false
      pathcalculatorTool.resetDimensions()
      for (const s of state.sensors) {
        s.resetZoneFields(false)
      }
      this[`${mode}Element`].classList.add('selected')
      this.attach()
    } else {
      this.reset()
      this[`${mode}Element`].classList.remove('selected')
      this.detach()
    }
    Renderer.render()
  }

  closeActiveMode () {
    this[`${state.tracerMode}Element`].classList.remove('selected')
    this.reset()
    this.detach()
  }

  removePath () {
    this.closeActiveMode()
    Renderer.render()
  }

  async traceAndSetPath () {
    const [aWorld, bWorld] = state.tracerPointsWorld
    const w = viewport.canvas.width
    const h = viewport.canvas.height

    try {
      const cost = buildCostMap(viewport.ctx, w, h, { gamma: 2.5 })

      const start = this.worldToCanvasBacking(aWorld)
      const goal = this.worldToCanvasBacking(bWorld)

      const pad = 80
      const corridor = {
        minX: Math.max(0, Math.min(start.x, goal.x) - pad),
        maxX: Math.min(w - 1, Math.max(start.x, goal.x) + pad),
        minY: Math.max(0, Math.min(start.y, goal.y) - pad),
        maxY: Math.min(h - 1, Math.max(start.y, goal.y) + pad)
      }

      let path = await traceAStar(cost, w, h, start, goal, {
        corridor,
        turnPenalty: 0.2
      })

      if (!path) {
        loader.setStatusMessage('failure', 'No path found.')
        this.removePath()
        return
      }

      path = removeJaggedness(path, cost, w, h)

      const pathWorld = path.map(p =>
        screenCssToWorld(canvasBackingToCss(p))
      )

      const lenPx = pathLengthPx(pathWorld)

      state.tracerPathWorld = pathWorld
      state.lenPath = lenPx
      state.lenUnit = 'px'

      Renderer.render()
    } catch (err) {
      loader.setStatusMessage('failure', `Error tracing path. [${err.message}]`)
      setTimeout(() => this.removePath(), 2000)
    }
  }

  traceLinearPath () {
    state.tracerPathWorld = [state.tracerPointsWorld[0], state.tracerPointsWorld[1]]
    state.lenPath = Math.hypot(
      state.tracerPointsWorld[1].x - state.tracerPointsWorld[0].x,
      state.tracerPointsWorld[1].y - state.tracerPointsWorld[0].y
    )
    state.lenUnit = 'px'
    Renderer.render()
  }

  worldToCanvasBacking (wp) {
    const sp = worldToScreen(wp.x, wp.y)
    const rect = viewport.canvas.getBoundingClientRect()
    const scaleX = viewport.canvas.width / rect.width // ~DPR
    const scaleY = viewport.canvas.height / rect.height // ~DPR
    return { x: Math.round(sp.x * scaleX), y: Math.round(sp.y * scaleY) }
  }

  findNearestEdge (point, radius = 50, threshold = 40) {
    const canvas = viewport.canvas
    const ctx = viewport.ctx

    const scaleX = canvas.width / canvas.getBoundingClientRect().width
    const scaleY = canvas.height / canvas.getBoundingClientRect().height

    const px = Math.round(point.x * scaleX)
    const py = Math.round(point.y * scaleY)

    const minX = Math.max(1, px - radius)
    const minY = Math.max(1, py - radius)
    const maxX = Math.min(canvas.width - 2, px + radius)
    const maxY = Math.min(canvas.height - 2, py + radius)

    const width = maxX - minX + 1
    const height = maxY - minY + 1
    if (width <= 2 || height <= 2) return null

    const image = ctx.getImageData(minX, minY, width, height)
    const data = image.data

    const lum = (x, y) => {
      const i = (y * width + x) * 4
      return (
        0.2126 * data[i] +
                0.7152 * data[i + 1] +
                0.0722 * data[i + 2]
      )
    }

    let best = null
    let bestDist = Infinity

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const gx = Math.abs(lum(x + 1, y) - lum(x - 1, y))
        const gy = Math.abs(lum(x, y + 1) - lum(x, y - 1))
        const edgeStrength = gx + gy

        if (edgeStrength < threshold) continue

        const cx = minX + x
        const cy = minY + y
        const dist = Math.hypot(cx - px, cy - py)

        if (dist < bestDist) {
          bestDist = dist
          best = { x: cx / scaleX, y: cy / scaleY }
        }
      }
    }

    return best
  }
}

export let pathTracerTool = null

export function initPathTracerTool () {
  pathTracerTool = new PathTracerTool()
  return pathTracerTool
}
